/**
 * Global JavaScript - My Own Theme
 * Core functionality and utility functions
 * Pure Vanilla JS ES6+ - No jQuery
 */

// ========================================
// PUBSUB - Event System
// ========================================
const PubSub = {
    events: {},

    subscribe(event, callback) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(callback);

        // Return unsubscribe function
        return () => {
            this.events[event] = this.events[event].filter(cb => cb !== callback);
        };
    },

    publish(event, data = {}) {
        if (!this.events[event]) return;
        this.events[event].forEach(callback => callback(data));
    }
};

// Theme namespace
window.MyTheme = window.MyTheme || {};
window.MyTheme.PubSub = PubSub;
window.PubSub = PubSub;

// ========================================
// CART API
// ========================================
class CartAPI {
    constructor() {
        this.routes = {
            cart: '/cart.js',
            add: '/cart/add.js',
            change: '/cart/change.js',
            update: '/cart/update.js',
            clear: '/cart/clear.js'
        };
    }

    async getCart() {
        try {
            const response = await fetch(this.routes.cart, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return await response.json();
        } catch (error) {
            console.error('Error fetching cart:', error);
            throw error;
        }
    }

    async addItem(id, quantity = 1, properties = {}) {
        try {
            const response = await fetch(this.routes.add, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id,
                    quantity,
                    properties
                })
            });

            const data = await response.json();

            if (response.ok) {
                PubSub.publish('cart:item-added', data);
                this.updateCartCount();
                return data;
            } else {
                throw new Error(data.description || 'Error adding item to cart');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    }

    async updateItem(key, quantity) {
        try {
            const response = await fetch(this.routes.change, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: key,
                    quantity
                })
            });

            const data = await response.json();

            if (response.ok) {
                PubSub.publish('cart:updated', data);
                this.updateCartCount();
                return data;
            } else {
                throw new Error(data.description || 'Error updating cart');
            }
        } catch (error) {
            console.error('Error updating cart:', error);
            throw error;
        }
    }

    async removeItem(key) {
        return this.updateItem(key, 0);
    }

    async updateCartCount() {
        const cart = await this.getCart();
        PubSub.publish('cart:count-updated', { count: cart.item_count });

        // Update all cart count elements
        document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = cart.item_count;
            el.classList.toggle('hidden', cart.item_count === 0);
        });
    }
}

// Initialize global cart instance
window.MyTheme.cart = new CartAPI();
window.cart = window.MyTheme.cart;

// ========================================
// QUICK ADD (single delegated listener — do not duplicate per product card)
// ========================================
document.addEventListener('click', async (e) => {
    const quickAddBtn = e.target.closest('[data-quick-add]');
    if (!quickAddBtn) return;

    e.preventDefault();
    const productId = quickAddBtn.dataset.productId;

    quickAddBtn.disabled = true;
    quickAddBtn.classList.add('loading');

    try {
        await window.cart.addItem(productId, 1);
        PubSub.publish('cart:open-drawer');
    } catch (error) {
        console.error('Error adding to cart:', error);
    } finally {
        quickAddBtn.disabled = false;
        quickAddBtn.classList.remove('loading');
    }
});

// ========================================
// UTILITY FUNCTIONS
// ========================================
const Utils = {
    /**
     * Debounce function
     */
    debounce(fn, delay = 300) {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    },

    /**
     * Throttle function
     */
    throttle(fn, limit = 300) {
        let inThrottle;
        return (...args) => {
            if (!inThrottle) {
                fn.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    },

    /**
     * Format money (Shopify format)
     */
    formatMoney(cents, format = '${{amount}}') {
        if (typeof cents === 'string') {
            cents = cents.replace('.', '');
        }

        const value = (cents / 100).toFixed(2);
        const [dollars, pennies] = value.split('.');

        return format
            .replace('{{amount}}', value)
            .replace('{{amount_no_decimals}}', dollars)
            .replace('{{amount_with_comma_separator}}', `${dollars},${pennies}`);
    },

    /**
     * Serialize form data
     */
    serializeForm(form) {
        const formData = new FormData(form);
        const obj = {};
        formData.forEach((value, key) => {
            if (obj[key]) {
                if (!Array.isArray(obj[key])) {
                    obj[key] = [obj[key]];
                }
                obj[key].push(value);
            } else {
                obj[key] = value;
            }
        });
        return obj;
    },

    /**
     * Get URL parameters
     */
    getUrlParams() {
        return Object.fromEntries(new URLSearchParams(window.location.search));
    },

    /**
     * Update URL parameter without page reload
     */
    updateUrlParam(key, value) {
        const url = new URL(window.location);
        if (value) {
            url.searchParams.set(key, value);
        } else {
            url.searchParams.delete(key);
        }
        window.history.pushState({}, '', url);
    },

    /**
     * Trap focus within element (for modals/drawers)
     */
    trapFocus(container, elementToFocus = container) {
        const focusableElements = container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        elementToFocus.focus();

        container.addEventListener('keydown', (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    lastFocusable.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusable) {
                    firstFocusable.focus();
                    e.preventDefault();
                }
            }
        });
    },

    /**
     * Pause media (video/audio) in element
     */
    pauseMedia(container) {
        container.querySelectorAll('video, audio').forEach(media => {
            media.pause();
        });
        container.querySelectorAll('iframe').forEach(iframe => {
            if (iframe.contentWindow && iframe.src) {
                let targetOrigin;
                try {
                    targetOrigin = new URL(iframe.src).origin;
                } catch (e) {
                    return;
                }
                iframe.contentWindow.postMessage(
                    JSON.stringify({ event: 'command', func: 'pauseVideo' }),
                    targetOrigin
                );
            }
        });
    }
};

window.MyTheme.Utils = Utils;
window.Utils = Utils;

// ========================================
// SECTION RENDERING (for AJAX)
// ========================================
class SectionRenderer {
    constructor() {
        this.cache = new Map();
    }

    /**
     * Fetch a section from the server
     */
    async fetchSection(sectionId, url = window.location.pathname) {
        const cacheKey = `${url}-${sectionId}`;

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            const fetchUrl = new URL(url, window.location.origin);
            fetchUrl.searchParams.set('section_id', sectionId);

            const response = await fetch(fetchUrl.toString());
            const html = await response.text();

            this.cache.set(cacheKey, html);
            return html;
        } catch (error) {
            console.error('Error fetching section:', error);
            throw error;
        }
    }

    /**
     * Replace section content on the page
     */
    async replaceSection(sectionId, url) {
        const html = await this.fetchSection(sectionId, url);
        const section = document.getElementById(`shopify-section-${sectionId}`);

        if (section) {
            const temp = document.createElement('div');
            temp.innerHTML = html;
            const newSection = temp.querySelector(`#shopify-section-${sectionId}`) || temp.firstElementChild;

            if (newSection) {
                section.innerHTML = newSection.innerHTML;
                PubSub.publish('section:replaced', { sectionId });
            }
        }

        return html;
    }

    clearCache() {
        this.cache.clear();
    }
}

window.MyTheme.sectionRenderer = new SectionRenderer();
window.sectionRenderer = window.MyTheme.sectionRenderer;

// ========================================
// INTERSECTION OBSERVER - Lazy Loading
// ========================================
const lazyLoadObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const el = entry.target;

                // Handle lazy images
                if (el.dataset.src) {
                    el.src = el.dataset.src;
                    el.removeAttribute('data-src');
                }

                if (el.dataset.srcset) {
                    el.srcset = el.dataset.srcset;
                    el.removeAttribute('data-srcset');
                }

                // Handle lazy backgrounds
                if (el.dataset.bgSrc) {
                    el.style.backgroundImage = `url(${el.dataset.bgSrc})`;
                    el.removeAttribute('data-bg-src');
                }

                el.classList.add('loaded');
                lazyLoadObserver.unobserve(el);
            }
        });
    },
    {
        rootMargin: '100px',
        threshold: 0.01
    }
);

// Initialize lazy loading
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-src], [data-srcset], [data-bg-src]').forEach(el => {
        lazyLoadObserver.observe(el);
    });
});

// ========================================
// SCROLL ANIMATIONS
// ========================================
const scrollAnimationObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                scrollAnimationObserver.unobserve(entry.target);
            }
        });
    },
    {
        rootMargin: '-50px',
        threshold: 0.1
    }
);

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-scroll-animate]').forEach(el => {
        scrollAnimationObserver.observe(el);
    });
});

// ========================================
// QUANTITY SELECTOR
// ========================================
class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input[type="number"]');
        this.decreaseBtn = this.querySelector('[data-decrease]');
        this.increaseBtn = this.querySelector('[data-increase]');

        if (!this.input) return;

        this.min = parseInt(this.input.min) || 1;
        this.max = parseInt(this.input.max) || 9999;
        this.step = parseInt(this.input.step) || 1;

        this.bindEvents();
    }

    bindEvents() {
        this.decreaseBtn?.addEventListener('click', () => this.decrease());
        this.increaseBtn?.addEventListener('click', () => this.increase());
        this.input.addEventListener('change', () => this.validate());
    }

    decrease() {
        const newValue = parseInt(this.input.value) - this.step;
        if (newValue >= this.min) {
            this.input.value = newValue;
            this.dispatchChangeEvent();
        }
    }

    increase() {
        const newValue = parseInt(this.input.value) + this.step;
        if (newValue <= this.max) {
            this.input.value = newValue;
            this.dispatchChangeEvent();
        }
    }

    validate() {
        let value = parseInt(this.input.value);
        if (isNaN(value) || value < this.min) {
            value = this.min;
        } else if (value > this.max) {
            value = this.max;
        }
        this.input.value = value;
    }

    dispatchChangeEvent() {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

customElements.define('quantity-input', QuantityInput);

// ========================================
// MODAL / DIALOG COMPONENT
// ========================================
class ModalDialog extends HTMLElement {
    constructor() {
        super();
        this.dialog = this.querySelector('dialog');
        this.openButtons = document.querySelectorAll(`[data-modal-open="${this.id}"]`);
        this.closeButtons = this.querySelectorAll('[data-modal-close]');

        this.bindEvents();
    }

    bindEvents() {
        this.openButtons.forEach(btn => {
            btn.addEventListener('click', () => this.open());
        });

        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        this.dialog?.addEventListener('click', (e) => {
            if (e.target === this.dialog) {
                this.close();
            }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.dialog?.open) {
                this.close();
            }
        });
    }

    open() {
        this.dialog?.showModal();
        document.body.style.overflow = 'hidden';
        Utils.trapFocus(this.dialog);
        PubSub.publish('modal:opened', { id: this.id });
    }

    close() {
        this.dialog?.close();
        document.body.style.overflow = '';
        Utils.pauseMedia(this.dialog);
        PubSub.publish('modal:closed', { id: this.id });
    }
}

customElements.define('modal-dialog', ModalDialog);

// ========================================
// DRAWER COMPONENT
// ========================================
class DrawerComponent extends HTMLElement {
    constructor() {
        super();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
        // Query elements after connection to ensure DOM is ready
        this.drawer = this.querySelector('[data-drawer]');
        this.overlay = this.querySelector('[data-drawer-overlay]');
        this.bindEvents();
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleKeydown);
    }

    bindEvents() {
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleKeydown);

        // Use event delegation for close buttons (handles dynamically added buttons)
        this.addEventListener('click', (e) => {
            // Check if clicked element or its parent is a close button
            const closeBtn = e.target.closest('[data-drawer-close]');
            if (closeBtn) {
                e.preventDefault();
                e.stopPropagation();
                this.close();
                return;
            }

            // Check if clicked on overlay
            const overlay = e.target.closest('[data-drawer-overlay]');
            if (overlay) {
                this.close();
                return;
            }
        });
    }

    handleDocumentClick(e) {
        const trigger = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (trigger) {
            e.preventDefault();
            this.open();
        }
    }

    handleKeydown(e) {
        if (e.key === 'Escape' && this.isOpen()) {
            this.close();
        }
    }

    isOpen() {
        return this.hasAttribute('open');
    }

    open() {
        this.previouslyFocused = document.activeElement;
        this.setAttribute('open', '');
        document.documentElement.setAttribute('data-drawer-open', 'true');
        document.body.style.overflow = 'hidden';
        document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'true');
        });
        if (this.drawer) {
            Utils.trapFocus(this.drawer);
        }
        PubSub.publish('drawer:opened', { id: this.id });
    }

    close() {
        this.removeAttribute('open');
        document.documentElement.removeAttribute('data-drawer-open');
        document.body.style.overflow = '';
        document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
        });
        if (this.drawer) {
            Utils.pauseMedia(this.drawer);
        }
        if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
            this.previouslyFocused.focus();
            this.previouslyFocused = null;
        }
        PubSub.publish('drawer:closed', { id: this.id });
    }
}

customElements.define('drawer-component', DrawerComponent);

// ========================================
// PRODUCT FORM
// ========================================
class ProductForm extends HTMLElement {
    constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.errorMessage = this.querySelector('[data-error-message]');

        if (this.form) {
            this.form.addEventListener('submit', this.onSubmit.bind(this));
        }
    }

    async onSubmit(e) {
        e.preventDefault();

        if (this.submitButton.disabled) return;

        this.setLoading(true);
        this.clearError();

        const formData = Utils.serializeForm(this.form);

        try {
            await window.cart.addItem(formData.id, parseInt(formData.quantity) || 1);

            // Success - handle based on cart type
            const cartType = this.dataset.cartType || 'drawer';

            if (cartType === 'drawer') {
                PubSub.publish('cart:open-drawer');
            } else {
                window.location.href = '/cart';
            }
        } catch (error) {
            this.showError(error.message);
        } finally {
            this.setLoading(false);
        }
    }

    setLoading(loading) {
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('loading', loading);
    }

    showError(message) {
        if (this.errorMessage) {
            this.errorMessage.textContent = message;
            this.errorMessage.classList.remove('hidden');
        }
    }

    clearError() {
        if (this.errorMessage) {
            this.errorMessage.textContent = '';
            this.errorMessage.classList.add('hidden');
        }
    }
}

customElements.define('product-form', ProductForm);

// ========================================
// VARIANT SELECTOR
// ========================================
class VariantSelector extends HTMLElement {
    constructor() {
        super();
        this.productData = JSON.parse(this.dataset.productJson || '{}');
        this.bindEvents();
    }

    bindEvents() {
        this.querySelectorAll('input[type="radio"], select').forEach(input => {
            input.addEventListener('change', () => this.onVariantChange());
        });
    }

    getSelectedOptions() {
        const options = [];
        this.querySelectorAll('fieldset, .variant-option').forEach((group, index) => {
            const selected = group.querySelector('input:checked, select');
            if (selected) {
                options[index] = selected.value;
            }
        });
        return options;
    }

    findVariant(options) {
        return this.productData.variants?.find(variant => {
            return options.every((option, index) => variant.options[index] === option);
        });
    }

    onVariantChange() {
        const options = this.getSelectedOptions();
        const variant = this.findVariant(options);

        if (variant) {
            this.updateVariantId(variant.id);
            this.updatePrice(variant);
            this.updateAvailability(variant);
            this.updateUrl(variant.id);
            this.updateMedia(variant);

            PubSub.publish('variant:changed', { variant, productData: this.productData });
        }
    }

    updateVariantId(id) {
        const input = document.querySelector(`#${this.dataset.formId} input[name="id"]`);
        if (input) input.value = id;
    }

    updatePrice(variant) {
        const priceEl = document.querySelector(`#${this.dataset.priceId}`);
        if (priceEl) {
            PubSub.publish('price:update', { variant });
        }
    }

    updateAvailability(variant) {
        const submitBtn = document.querySelector(`#${this.dataset.formId} [type="submit"]`);
        if (submitBtn) {
            submitBtn.disabled = !variant.available;
            submitBtn.textContent = variant.available
                ? submitBtn.dataset.addText
                : submitBtn.dataset.soldoutText;
        }
    }

    updateUrl(variantId) {
        if (!this.dataset.updateUrl) return;
        Utils.updateUrlParam('variant', variantId);
    }

    updateMedia(variant) {
        if (variant.featured_media) {
            PubSub.publish('variant:media-change', {
                mediaId: variant.featured_media.id,
                variant
            });
        }
    }
}

customElements.define('variant-selector', VariantSelector);

// ========================================
// INITIALIZATION
// ========================================
document.addEventListener('DOMContentLoaded', () => {
    // Initialize cart count on page load
    window.cart.updateCartCount();

    // Log theme info
    console.log('%c My Own Theme ', 'background: #8B2500; color: white; padding: 4px 8px; border-radius: 4px;');
});
